import classnames from 'classnames';
import { addDays, getUnixTime, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { formattedDateString } from 'utils/dateUtils';
import { openInput } from '../../../actions/baseActions';
import { setCheckInDate, setCheckOutDate } from '../../../actions/hotelActions';
import { emitDateClickGAEvent, emitFromDateGAEvent } from '../../../utils/ga-event-helpers';
import DatePicker from '../Common/DatePicker';
import FadeTransition from '../Common/FadeTransition';

type CheckInDateProps = {
	checkOutDate: Date;
	checkInDate: Date;
	name: string;
	openedInput?: string;
	autoJump?: boolean;
	preventSetInputBoxClassName?: boolean;
	lng: string;
	postfix?: string;
	setCheckInDate: (date: Date) => void;
	setCheckOutDate: (date: Date) => void;
	openInput: (name?: string) => void;
};

type CheckInDateState = {
	in: boolean;
};

class CheckInDate extends React.Component<CheckInDateProps, CheckInDateState> {
	constructor(props: CheckInDateProps) {
		super(props);
		this.state = {
			in: false,
		};
	}

	onSelectDate = (from: Date, keyboardNavigation = false) => {
		let to = this.props.checkOutDate;
		if (from === null) {
			from = new Date();
		} else {
			from = parseISO(from.toISOString());
		}
		if (to === null) {
			to = addDays(startOfDay(from), 1);
		} else {
			to = parseISO(to.toISOString());
		}
		const fromDate = getUnixTime(from);
		const toDate = getUnixTime(to);

		if (isSameDay(from, to)) {
			to = addDays(from, 1);
		}

		if (isAfter(fromDate, toDate)) {
			to = from;
			to = addDays(from, 1);
		}
		this.props.setCheckInDate(from);
		this.props.setCheckOutDate(to);

		if (keyboardNavigation) {
			return;
		}
		// Emit only on a committed selection, not per arrow-key reposition (A6).
		emitFromDateGAEvent('hotels');
		this.openCalendar(true);
	};

	openCalendar = (openToDateCalendar?: boolean, fromUserClick: boolean = false) => {
		openToDateCalendar = openToDateCalendar || false;
		if (fromUserClick) {
			emitDateClickGAEvent('hotels');
		}
		this.setState(
			{
				in: !this.state.in,
			},
			() => {
				if (this.state.in) {
					this.props.openInput(this.props.name);
				} else if (openToDateCalendar) {
					if (this.props.autoJump) this.props.openInput(`check-out-date${this.props.postfix}`);
					else this.props.openInput();
				}
			}
		);
	};

	componentDidUpdate(prevProps: CheckInDateProps) {
		if (this.props.openedInput !== prevProps.openedInput) {
			if (!this.props.openedInput) {
				this.setState({
					in: false,
				});
			} else if (this.props.openedInput === this.props.name) {
				this.setState({
					in: true,
				});
			}
		}
	}

	render() {
		const { lng, name, openedInput, preventSetInputBoxClassName, checkInDate } = this.props;
		const formattedDate = formattedDateString({ lng, date: checkInDate });

		return (
			<div
				id={name}
				className={classnames('flex-1', {
					'open-input': openedInput === name,
					'input-box': !preventSetInputBoxClassName,
				})}
			>
				<div
					className={classnames('input-control justify-content-between filled', {
						focused: openedInput === name,
					})}
					aria-label={formattedDate.ariaLabelString}
				>
					<div className="date-picker d-flex align-items-center flex-1" onClick={() => this.openCalendar(false, true)}>
						<i className="icon-date" />
						<div className="selected">
							{formattedDate.renderText}
						</div>
					</div>
				</div>
				<div style={{ position: 'relative' }}>
					<FadeTransition timeout={500} in={openedInput === name}>
						<DatePicker before={new Date()} selected={checkInDate as Date} onSelectDate={this.onSelectDate} />
					</FadeTransition>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	checkInDate: state.hotel.checkInDate,
	checkOutDate: state.hotel.checkOutDate,
	openedInput: state.base.openedInput,
	autoJump: state.base.autoJump,
});

export default connect(mapStateToProps, { setCheckInDate, openInput, setCheckOutDate })(CheckInDate);
